
import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, AlertTriangle, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SubscriptionStatusProps {
  status: 'processing' | 'active' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'canceled' | 'unpaid' | 'past_due' | 'paused' | 'requires_action' | null;
  errorMessage?: string;
  onRetry?: () => void;
}

const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({ status, errorMessage, onRetry }) => {
  const { t } = useLanguage();
  
  const renderContent = () => {
    switch(status) {
      case 'processing':
        return (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t('subscription.please_wait')}</h2>
          </div>
        );
      
      case 'active':
        return (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('subscription.success_title')}</h2>
            <p className="text-muted-foreground mb-4">{t('subscription.success_description')}</p>
            <Button onClick={() => window.location.href = '/my-account'}>
              {t('subscription.go_to_account')}
            </Button>
          </div>
        );
        
      case 'incomplete':
      case 'incomplete_expired':
      case 'past_due':
      case 'unpaid':
        return (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('subscription.error_title')}</h2>
            <p className="text-muted-foreground mb-4">
              {errorMessage || t('subscription.error_description')}
            </p>
            {onRetry && (
              <Button onClick={onRetry}>
                {t('subscription.retry')}
              </Button>
            )}
          </div>
        );
        
      case 'requires_action':
        return (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('subscription.action_required_title')}</h2>
            <p className="text-muted-foreground mb-4">{t('subscription.action_required_description')}</p>
            <Button onClick={() => window.location.reload()}>
              {t('subscription.complete_action')}
            </Button>
          </div>
        );
        
      case 'canceled':
        return (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-gray-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('subscription.canceled_title')}</h2>
            <p className="text-muted-foreground mb-4">{t('subscription.canceled_description')}</p>
            <Button onClick={() => window.location.href = '/pricing'}>
              {t('subscription.view_plans')}
            </Button>
          </div>
        );
        
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-gray-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t('subscription.unknown_status_title')}</h2>
            <p className="text-muted-foreground mb-4">{t('subscription.unknown_status_description')}</p>
            <Button onClick={() => window.location.href = '/my-account'}>
              {t('subscription.go_to_account')}
            </Button>
          </div>
        );
    }
  };
  
  return (
    <Card className="w-full max-w-md mx-auto overflow-hidden">
      {renderContent()}
    </Card>
  );
};

export default SubscriptionStatus;
